import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonService } from '../../../../../../shared/services/common.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MasterManagementService } from '../../../../../management/master-management/master-management.service';

@Component({
  selector: 'app-sensor-type',
  templateUrl: './sensor-type.component.html',
  styleUrls: ['./sensor-type.component.css']
})
export class SensorTypeComponent implements OnInit {
  customer_id: string;
  public sensorTypes: any[] = [];
  isLoading: boolean = false;

  isModalVisible: boolean = false;
  isFormLoading: boolean = false;
  isEditMode: boolean = false;
  editingSensorType: any = null;
  sensorTypeForm!: FormGroup;
  user_id = localStorage.getItem('user_id');

  listOfColumns = [{
    name: 'ID'
  }, {
    name: 'Abbreviation'
  }, {
    name: 'Sensor Type'
  }, {
    name: 'Action'
  }]

  constructor(
    private commonService: CommonService,
    private masterManagementService: MasterManagementService,
    public route: ActivatedRoute,
    private fb: FormBuilder
  ) {
    this.customer_id = this.route.snapshot.paramMap.get('id') || '';
    if (!this.customer_id) {
      this.customer_id = this.route.parent?.snapshot.paramMap.get('id') || '';
    }
    if (!this.customer_id) {
      this.customer_id = this.route.root.firstChild?.snapshot.paramMap.get('id') || '';
    }
    if (!this.customer_id) {
      this.customer_id = '4';
    }
  }

  ngOnInit() {
    this.initializeForm();
    this.getSensorTypes();
  }

  initializeForm(): void {
    this.sensorTypeForm = this.fb.group({
      sensorType: [null, [Validators.required, Validators.maxLength(50)]],
      abbreviation: [null, [Validators.required, Validators.maxLength(3)]],
      temperatureColor: ['#000000', [Validators.required]],
      humidityColor: ['#000000', [Validators.required]],
      pressureColor: ['#000000', [Validators.required]]
    });
  }

  getSensorTypes() {
    this.isLoading = true;
    this.masterManagementService.getAbbreviationsByCustomerId(this.customer_id).subscribe({
      next: (res: any) => {
        if (res && Array.isArray(res)) {
          this.sensorTypes = res;
        } else if (res && res.data && Array.isArray(res.data)) {
          this.sensorTypes = res.data;
        } else {
          this.sensorTypes = [];
        }
        this.isLoading = false;
      },
      error: (err: any) => {
        console.error('Error fetching sensor types:', err);
        this.commonService.message.error('Failed to fetch sensor types');
        this.isLoading = false;
      }
    });
  }

  deleteSensorType(id: number, confirmed: boolean = false) {
    const executeDelete = () => {
      this.isLoading = true;
      const payload = { status: 'discard', customer_id: parseInt(this.customer_id) };

      this.masterManagementService.deleteAbbreviation(id, payload).subscribe({
        next: (res: any) => {
          if (res.status === 200) {
            this.commonService.message.success('Sensor type deleted successfully');
            this.getSensorTypes();
          } else {
            this.commonService.message.error(res.message || 'Failed to delete sensor type');
          }
          this.isLoading = false;
        },
        error: (err: any) => {
          console.error('Error deleting sensor type:', err);
          this.commonService.message.error('Failed to delete sensor type');
          this.isLoading = false;
        }
      });
    };

    if (confirmed) {
      executeDelete();
      return;
    }

    this.commonService.modalSvc.confirm({
      nzTitle: 'Delete Sensor Type',
      nzContent: 'Are you sure you want to delete this sensor type?',
      nzOkText: 'Yes, Delete',
      nzOkType: 'primary',
      nzOkDanger: true,
      nzCancelText: 'Cancel',
      nzOnOk: () => executeDelete()
    });
  }

  editSensorType(sensorType: any) {
    this.isModalVisible = true;
    this.isEditMode = true;
    this.editingSensorType = sensorType;

    if (this.sensorTypeForm) {
      this.sensorTypeForm.patchValue({
        sensorType: sensorType.description,
        abbreviation: sensorType.abbreviation,
        temperatureColor: sensorType.color_code?.temperature || '#000000',
        humidityColor: sensorType.color_code?.humidity || '#000000',
        pressureColor: sensorType.color_code?.pressure || '#000000'
      });
    }
  }

  addSensorType() {
    this.isModalVisible = true;
    this.isEditMode = false;
    this.editingSensorType = null;
    if (this.sensorTypeForm) {
      this.sensorTypeForm.reset();
      this.sensorTypeForm.patchValue({
        temperatureColor: '#000000',
        humidityColor: '#000000',
        pressureColor: '#000000'
      });
    }
  }

  validateSensorType(): void {
    const sensorType = this.sensorTypeForm.get('sensorType')?.value;
    if (sensorType && sensorType.trim()) {
      if (this.isEditMode && this.editingSensorType) {
        if (sensorType.toLowerCase() === this.editingSensorType.description?.toLowerCase()) {
          this.sensorTypeForm.get('sensorType')?.setErrors(null);
          return;
        }
      }

      const isDuplicate = this.sensorTypes.some(st =>
        st.description?.toLowerCase() === sensorType.toLowerCase()
      );
      if (isDuplicate) {
        this.sensorTypeForm.get('sensorType')?.setErrors({ duplicate: true });
      } else {
        this.sensorTypeForm.get('sensorType')?.setErrors(null);
      }
    }
  }

  validateAbbreviation(): void {
    const abbreviation = this.sensorTypeForm.get('abbreviation')?.value;
    if (abbreviation && abbreviation.trim()) {
      if (this.isEditMode && this.editingSensorType) {
        if (abbreviation.toLowerCase() === this.editingSensorType.abbreviation?.toLowerCase()) {
          this.sensorTypeForm.get('abbreviation')?.setErrors(null);
          return;
        }
      }

      const isDuplicate = this.sensorTypes.some(st =>
        st.abbreviation?.toLowerCase() === abbreviation.toLowerCase()
      );
      if (isDuplicate) {
        this.sensorTypeForm.get('abbreviation')?.setErrors({ duplicate: true });
      } else {
        this.sensorTypeForm.get('abbreviation')?.setErrors(null);
      }
    }
  }

  validateWithAPI(): Promise<{ isValid: boolean; errorMessage?: string }> {
    return new Promise((resolve) => {
      const abbreviation = this.sensorTypeForm.get('abbreviation')?.value;
      const description = this.sensorTypeForm.get('sensorType')?.value;

      if (!abbreviation || !description) {
        resolve({ isValid: false, errorMessage: 'Please fill in all required fields' });
        return;
      }

      if (this.isEditMode && this.editingSensorType) {
        const abbreviationChanged = abbreviation.toLowerCase() !== this.editingSensorType.abbreviation?.toLowerCase();
        const descriptionChanged = description.toLowerCase() !== this.editingSensorType.description?.toLowerCase();
        if (!abbreviationChanged && !descriptionChanged) {
          resolve({ isValid: true });
          return;
        }
      }

      this.masterManagementService.validateAbbreviation(this.customer_id, abbreviation, description).subscribe({
        next: (res: any) => {
          if (res.status === 200 && res.data?.isValid === true) {
            resolve({ isValid: true });
          } else {
            this.sensorTypeForm.get('abbreviation')?.setErrors({ apiValidation: true });
            this.sensorTypeForm.get('sensorType')?.setErrors({ apiValidation: true });
            resolve({ isValid: false, errorMessage: res?.message || 'Abbreviation or Sensor Type already exists' });
          }
        },
        error: (err: any) => {
          console.error('Validation API error:', err);
          const errorMessage = err?.error?.message || err?.message || 'Validation API failed. Please try again.';
          resolve({ isValid: false, errorMessage });
        }
      });
    });
  }

  handleCancel(): void {
    this.isModalVisible = false;
    this.isEditMode = false;
    this.editingSensorType = null;
    this.sensorTypeForm.reset();
  }

  async submitForm(): Promise<void> {
    this.validateSensorType();
    this.validateAbbreviation();

    if (!this.sensorTypeForm.valid) {
      Object.values(this.sensorTypeForm.controls).forEach(control => {
        control.markAsDirty();
        control.updateValueAndValidity();
      });
      return;
    }

    const validationResult = await this.validateWithAPI();
    if (!validationResult.isValid) {
      this.commonService.message.error(validationResult.errorMessage || 'Validation failed');
      return;
    }

    this.isFormLoading = true;
    const formData = this.sensorTypeForm.value;

    if (this.isEditMode) {
      const payload = {
        customer_id: parseInt(this.customer_id),
        abbreviation: formData.abbreviation,
        description: formData.sensorType,
        color_code: {
          temperature: formData.temperatureColor,
          humidity: formData.humidityColor,
          pressure: formData.pressureColor
        }
      };

      this.masterManagementService.updateAbbreviation(this.editingSensorType.id, payload).subscribe({
        next: (res: any) => {
          if (res.status === 200) {
            this.commonService.message.success('Sensor type updated successfully');
            this.isModalVisible = false;
            this.isEditMode = false;
            this.editingSensorType = null;
            this.sensorTypeForm.reset();
            this.getSensorTypes();
          } else {
            this.commonService.message.error(res.message || 'Failed to update sensor type');
          }
          this.isFormLoading = false;
        },
        error: (err: any) => {
          console.error('Error updating sensor type:', err);
          this.commonService.message.error('Failed to update sensor type');
          this.isFormLoading = false;
        }
      });
    } else {
      const payload = {
        customer_id: parseInt(this.customer_id),
        abbreviation: formData.abbreviation,
        description: formData.sensorType,
        color_code: {
          temperature: formData.temperatureColor,
          humidity: formData.humidityColor,
          pressure: formData.pressureColor
        },
        status: 'active',
        created_by_user_id: parseInt(this.user_id || '0')
      };

      this.masterManagementService.addAbbreviation(payload).subscribe({
        next: (res: any) => {
          if (res.status === 200) {
            this.commonService.message.success('Sensor type added successfully');
            this.isModalVisible = false;
            this.sensorTypeForm.reset();
            this.getSensorTypes();
          } else {
            this.commonService.message.error(res.message || 'Failed to add sensor type');
          }
          this.isFormLoading = false;
        },
        error: (err: any) => {
          console.error('Error creating sensor type:', err);
          this.commonService.message.error('Failed to add sensor type');
          this.isFormLoading = false;
        }
      });
    }
  }
}
